import { HttpClient, HttpClientModule } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { AngularFireModule } from '@angular/fire';
import { AngularFireAuthModule } from '@angular/fire/auth';
import { LazyLoadImageModule } from 'ng-lazyload-image';
import { BrowserModule, BrowserTransferStateModule, TransferState } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AuthDataAccessModule, AuthState } from '@fitscovery/auth/data-access';
import { UtilsTokenInterceptorModule } from '@fitscovery/utils/token-interceptor';
import { EventsDataAccessModule, EVENT_STATE_TOKEN } from '@fitscovery/events/data-access';
import { ApiMembershipService, MembershipState } from '@fitscovery/membership/data-access';
import { UtilSubdomain } from '@fitscovery/partner-web/shared/utils';
import { PartnerMembershipSubscriptionState, PartnerState, PartnerDataAccessModule } from '@fitscovery/partner/data-access';
import { ApiPaymongoService, PaymentState, ApiXendItService } from '@fitscovery/payments/data-access';
import { ApiPostService, PostState, PostUserPointsState } from '@fitscovery/posts/data-access';
import { SharedPartnerWebUiBottomNavbarModule } from '@fitscovery/partner-web/ui/ui-bottom-navbar';
import { SharedPartnerWebUiFooterModule } from '@fitscovery/partner-web/ui/ui-footer';
import { SharedPartnerWebUiNavbarModule } from '@fitscovery/partner-web/ui/ui-navbar';
import { HttpClientService } from '@fitscovery/partner-web/shared/services/http';
import { UserState, UserDataAccessModule } from '@fitscovery/user/data-access';
import { PartnerWebScheduleState, PartnerWebSchedulesStoreModule } from '@fitscovery/partner-web/schedules/store';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { NgxsLoggerPluginModule } from '@ngxs/logger-plugin';
import { NgxsStoragePluginModule, StorageOption } from '@ngxs/storage-plugin';
import { getActionTypeFromInstance, NgxsModule } from '@ngxs/store';
import { QuillModule } from 'ngx-quill';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { PartnerWebWorkoutsFeaturesWorkoutListModule } from '@fitscovery/partner-web/workouts/workout-list';
import { SharedStoreModule, SHARED_STORE_STATE_TOKEN } from '@fitscovery/shared/store';
import { ApplicationState, PushTitleHeight } from '@fitscovery/partner-web/store/application';
import { translateBrowserLoaderFactory } from './translate-browser.loader';
import { TransferHttpCacheModule } from '@nguniversal/common';
import { PartnerWebLogoutPluginProvider } from '@fitscovery/utils/ngxs-plugins/logout';

const filterActionInNgxsLoggerPlugin = (action: any) => 
  getActionTypeFromInstance(action) !== PushTitleHeight.type

const IMPORT_STATES = [
  AuthState,
  MembershipState,
  PartnerState,
  PartnerMembershipSubscriptionState,
  PartnerWebScheduleState,
  PaymentState,
  PostState,
  PostUserPointsState,
  UserState,
  ApplicationState
];

const STORED_STATES = [
  AuthState,
  EVENT_STATE_TOKEN,
  MembershipState,
  PartnerState,
  SHARED_STORE_STATE_TOKEN,
  ApplicationState,
  PartnerMembershipSubscriptionState,
  PartnerWebScheduleState
];

export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

@NgModule({
  declarations: [
    AppComponent
  ],
  imports: [
    BrowserModule.withServerTransition({ appId: 'PartnerNgServer' }),
    TransferHttpCacheModule,
    BrowserAnimationsModule,
    BrowserTransferStateModule,
    HttpClientModule,
    AppRoutingModule,
    FontAwesomeModule,
    LazyLoadImageModule,
    SharedPartnerWebUiNavbarModule,
    SharedPartnerWebUiFooterModule,
    SharedPartnerWebUiBottomNavbarModule,
    AngularFireModule.initializeApp(environment.firebase),
    AngularFireAuthModule,
    QuillModule.forRoot(),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: translateBrowserLoaderFactory,
        deps: [HttpClient, TransferState]
      }
    }),
    NgxsModule.forRoot(IMPORT_STATES, { developmentMode: !environment.production }),
    NgxsStoragePluginModule.forRoot({
      key: STORED_STATES,
      storage: StorageOption.SessionStorage
    }),
    NgxsReduxDevtoolsPluginModule.forRoot(),
    NgxsLoggerPluginModule.forRoot({
      disabled: environment.production,
      filter: filterActionInNgxsLoggerPlugin
    }),
    AuthDataAccessModule.forRoot(environment),
    UserDataAccessModule.forRoot(environment),
    PartnerDataAccessModule.forRoot(environment),
    EventsDataAccessModule.forRoot(environment),
    PartnerWebSchedulesStoreModule,
    SharedStoreModule,
    PartnerWebWorkoutsFeaturesWorkoutListModule.forRoot(environment),
    UtilsTokenInterceptorModule.forRoot(environment)
  ],
  // TODO: This can be removed since we do not need to have a HttpClientService.
  // We can just use an interceptor to add the Authorization header.
  // Will not remove for now since it might cause breaking changes. -Francis
  providers: [
    HttpClientService,
    ApiPostService,
    ApiMembershipService,
    ApiPaymongoService,
    ApiXendItService,
    UtilSubdomain,
    PartnerWebLogoutPluginProvider
  ],
  bootstrap: [AppComponent],
})
export class AppModule { }
